import { createSlice } from "@reduxjs/toolkit";
import { defaultSocialAccountContentFilters, defaultSocialAccountCreatorsFilters, defaultTrackedPostsFilters, SocialAccountState } from "types/social-account";
import { fetchSocialAccountTrackedPostsThunk, fetchSocialAccountThunk, fetchSocialAccountCreatorsThunk, fetchSocialAccountContentThunk } from "./thunk";
import { ERROR } from "types";

const initialState: SocialAccountState = {
    loading: false,
    error: null,
    list: {
        loading: false,
        last_fetched_at: 0,
        items: [],
        error: null
    },
    items: {},
    trackedPosts: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultTrackedPostsFilters
    },
    creators: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultSocialAccountCreatorsFilters
    },
    content: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultSocialAccountContentFilters
    }
}

const socialAccountState = createSlice({
    name: 'socialAccountState',
    initialState,
    reducers: {
        postAddedToCampaign(state, action) {
            const { socialAccountId, collaboration, trackedPostId } = action.payload;
            const filteredLists = state.trackedPosts.items[socialAccountId]?.filtered_lists ?? {};
            Object.keys(filteredLists).forEach((filterId) => {
                const list = filteredLists[filterId];
                const updatedItems = list.items.map((trackedPost) => {
                    if (trackedPost.social_post.uuid === trackedPostId) {
                        return {
                            ...trackedPost,
                            social_post: {
                                ...trackedPost.social_post,
                                collaboration: collaboration
                            }
                        }
                    }
                    return trackedPost;
                });
                list.items = updatedItems;
            });
            state.trackedPosts.items[socialAccountId] = {
                ...state.trackedPosts.items[socialAccountId],
                filtered_lists: filteredLists
            };
        },
        setTrackedPostsFilters(state, action) {
            state.trackedPosts.filters = action.payload;
        },
        cleanTrackedPostState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.trackedPosts.items[socialAccountId];
        },
        setCreatorsFilters(state, action) {
            state.creators.filters = action.payload;
        },
        cleanCreatorsState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.creators.items[socialAccountId];
        },
        setContentFilters(state, action) {
            state.content.filters = action.payload;
        },
        cleanContentState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.content.items[socialAccountId];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSocialAccountThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSocialAccountThunk.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.error = null;
                const { socialAccountId, socialAccount, cached } = payload;
                state.items[socialAccountId] = {
                    last_fetched_at: cached ? (state.items[socialAccountId]?.last_fetched_at ?? 0) : Date.now(),
                    account: socialAccount
                }
            })
            .addCase(fetchSocialAccountThunk.rejected, (state) => {
                state.loading = false;
                state.error = "Failed to fetch social account"
            })

            .addCase(fetchSocialAccountTrackedPostsThunk.pending, (state) => {
                state.trackedPosts.loading = true;
                state.trackedPosts.error = null;
            })
            .addCase(fetchSocialAccountTrackedPostsThunk.fulfilled, (state, action) => {
                state.trackedPosts.loading = false;
                state.trackedPosts.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.trackedPosts.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.trackedPosts.items[socialAccountId] = {
                    ...state.trackedPosts.items[socialAccountId],
                    filtered_lists: {
                        ...(state.trackedPosts.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.count,
                            error: null,
                            loading: false
                        }
                    },
                    total: action.payload.count
                }
                state.trackedPosts.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountTrackedPostsThunk.rejected, (state, action) => {
                state.trackedPosts.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.trackedPosts.error = action.payload || "Failed to fetch applications"
            })
            .addCase(fetchSocialAccountCreatorsThunk.pending, (state) => {
                state.creators.loading = true;
                state.creators.error = null;
            })
            .addCase(fetchSocialAccountCreatorsThunk.fulfilled, (state, action) => {
                state.creators.loading = false;
                state.creators.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.creators.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.creators.items[socialAccountId] = {
                    ...state.creators.items[socialAccountId],
                    filtered_lists: {
                        ...(state.creators.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.count,
                            error: null,
                            loading: false
                        }
                    },
                    total: action.payload.count
                }
                state.creators.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountCreatorsThunk.rejected, (state, action) => {
                state.creators.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.creators.error = action.payload || "Failed to fetch social account creators"
            })
            .addCase(fetchSocialAccountContentThunk.pending, (state) => {
                state.content.loading = true;
                state.content.error = null;
            })
            .addCase(fetchSocialAccountContentThunk.fulfilled, (state, action) => {
                state.content.loading = false;
                state.content.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.content.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.content.items[socialAccountId] = {
                    ...state.content.items[socialAccountId],
                    filtered_lists: {
                        ...(state.content.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.count,
                            error: null,
                            loading: false
                        }
                    },
                    total: action.payload.count
                }
                state.content.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountContentThunk.rejected, (state, action) => {
                state.content.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.content.error = action.payload || "Failed to fetch content"
            })
    }
})

export const { postAddedToCampaign, setTrackedPostsFilters, cleanTrackedPostState, setCreatorsFilters, cleanCreatorsState, setContentFilters, cleanContentState } = socialAccountState.actions;

export default socialAccountState.reducer;